import { unref } from 'vue-demi'
import { type TypeNotExtends, type TypeExtends } from '../../../../../../types/helper'
import { error } from '../../../../../../utils/logger'
import { type DetailsTargetOptions } from '../../../settings/details-target-options'
import { type DefaultActivationDetailsRenderer } from './details'
import { type EmbedCSSStyles } from '../../../../../style'
import { getContainerStyle, getDetailsElementBounding } from './container-style'

// Lazy workaround for dependency injection
// fix it if getDetailsStyles needs to be used in multiple places
type GetDetailsStylesContext = Pick<DefaultActivationDetailsRenderer, 'windowSize' | 'videoElBounding' | 'ctx' | 'detailsTargetMap' | 'currentActivationPosition' | 'currentLayout'>

export const getDetailsStyles = (
  _targetOptions: DetailsTargetOptions | undefined,
  context: GetDetailsStylesContext
): {
  container: EmbedCSSStyles
  inner: EmbedCSSStyles
} => {
  const targetOptions = validateOption(_targetOptions, context)
  if (targetOptions == null) {
    // something went wrong, hide details
    return {
      container: { display: 'none' },
      inner: {}
    }
  }
  const elementBounding = getDetailsElementBounding(targetOptions, context)
  const container = getContainerStyle(elementBounding, context)
  const inner = {
    width: '100%',
    height: '100%',
    overflowY: 'scroll',
    ...context.currentLayout.value.details?.style
  }
  return {
    container,
    inner
  }
}

const validateOption = (option: DetailsTargetOptions | undefined, context: GetDetailsStylesContext): TypeNotExtends<DetailsTargetOptions, { mode: 'in-place' }> | undefined => {
  if (option == null) {
    error('Details target options are not defined.')
    return undefined
  }
  // Compute based on which target the activation preview is rendered in
  if (option.mode === 'in-place') {
    const inPlaceTargetOption = translateInPlaceOption(option, context)
    if (inPlaceTargetOption == null) {
      error('Details target options could not be computed for in-place mode.')
      return undefined
    }
    return inPlaceTargetOption
  } else {
    return option
  }
}

/**
   * Computes details target options for in-place mode.
   * Be careful with this return of this method, as it is used for recursive call.
   * @param targetOptions
   * @returns
   */
const translateInPlaceOption = (
  targetOptions: TypeExtends<DetailsTargetOptions, { mode: 'in-place' }>,
  context: GetDetailsStylesContext
): TypeNotExtends<DetailsTargetOptions, { mode: 'in-place' }> | undefined => {
  const itemPositionAndTarget = unref(context.currentActivationPosition)
  if (itemPositionAndTarget == null) {
    error('Item position and target could not be determined.')
    return undefined
  }
  if (itemPositionAndTarget.target === 'overlay') {
    return {
      mode: 'overlay-area',
      area: itemPositionAndTarget.position
    }
  }
  if (itemPositionAndTarget.target === 'outerOverlay') {
    return {
      mode: 'outer-overlay-area',
      area: itemPositionAndTarget.position
    }
  }
  if (itemPositionAndTarget.target === 'bottom') {
    return {
      mode: 'bottom',
      ...targetOptions.targets?.bottom
    }
  }
}
